/**
 * Logging endpoints for calls, emails, sms, meetings. v1 logs only — no
 * real send/receive. Each successful log writes a corresponding activity
 * row on the server side via @excellent/core/server.
 */

import { z } from "zod";
import type { Operation } from "../../types";

const okId = z.object({ id: z.number(), ok: z.literal(true) });

const callLog: Operation<
  void,
  void,
  {
    lead_id: number | null;
    direction: "inbound" | "outbound";
    status: "completed" | "no_answer" | "voicemail" | "busy" | "failed" | "logged";
    duration_sec?: number;
    notes?: string;
    occurred_at?: string;
    contact_person_id?: number | null;
    owner_actor_id?: number | null;
  },
  z.infer<typeof okId>
> = {
  method: "POST",
  path: () => "/api/crm/calls",
  body: z.object({
    lead_id: z.number().int().nullable(),
    direction: z.enum(["inbound", "outbound"]),
    status: z.enum([
      "completed",
      "no_answer",
      "voicemail",
      "busy",
      "failed",
      "logged",
    ]),
    duration_sec: z.number().int().nonnegative().optional(),
    notes: z.string().optional(),
    occurred_at: z.string().optional(),
    contact_person_id: z.number().int().nullable().optional(),
    owner_actor_id: z.number().int().nullable().optional(),
  }),
  response: okId,
};

const emailLog: Operation<
  void,
  void,
  {
    lead_id: number | null;
    direction: "sent" | "received" | "outbound_logged";
    subject: string;
    body_text: string;
    body_html?: string;
    occurred_at?: string;
    from_address?: string;
    to_addresses?: string[];
    template_id?: number | null;
    sequence_id?: number | null;
    owner_actor_id?: number | null;
  },
  z.infer<typeof okId>
> = {
  method: "POST",
  path: () => "/api/crm/emails",
  body: z.object({
    lead_id: z.number().int().nullable(),
    direction: z.enum(["sent", "received", "outbound_logged"]),
    subject: z.string().min(1),
    body_text: z.string(),
    body_html: z.string().optional(),
    occurred_at: z.string().optional(),
    from_address: z.string().optional(),
    to_addresses: z.array(z.string()).optional(),
    template_id: z.number().int().nullable().optional(),
    sequence_id: z.number().int().nullable().optional(),
    owner_actor_id: z.number().int().nullable().optional(),
  }),
  response: okId,
};

const smsLog: Operation<
  void,
  void,
  {
    lead_id: number | null;
    direction: "inbound" | "outbound";
    body: string;
    from_number?: string;
    to_number?: string;
    occurred_at?: string;
    owner_actor_id?: number | null;
  },
  z.infer<typeof okId>
> = {
  method: "POST",
  path: () => "/api/crm/sms",
  body: z.object({
    lead_id: z.number().int().nullable(),
    direction: z.enum(["inbound", "outbound"]),
    body: z.string().min(1),
    from_number: z.string().optional(),
    to_number: z.string().optional(),
    occurred_at: z.string().optional(),
    owner_actor_id: z.number().int().nullable().optional(),
  }),
  response: okId,
};

const meetingCreate: Operation<
  void,
  void,
  {
    lead_id: number | null;
    title: string;
    start_at: string;
    end_at: string;
    agenda?: string;
    location?: string;
    owner_actor_id?: number | null;
  },
  z.infer<typeof okId>
> = {
  method: "POST",
  path: () => "/api/crm/meetings",
  body: z.object({
    lead_id: z.number().int().nullable(),
    title: z.string().min(1),
    start_at: z.string(),
    end_at: z.string(),
    agenda: z.string().optional(),
    location: z.string().optional(),
    owner_actor_id: z.number().int().nullable().optional(),
  }),
  response: okId,
};

export const calls = { log: callLog };
export const emails = { log: emailLog };
export const sms = { log: smsLog };
export const meetings = { create: meetingCreate };
